import React, { useState, useEffect } from 'react';
import { AdminDashboard } from './AdminDashboard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { adminApi } from '../services/adminApi';
import { toast } from 'sonner';

export const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    icon: 'Briefcase',
    description: '',
    features: ['', '', '', '']
  });

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const data = await adminApi.getServices();
      setServices(data);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (service) => {
    setEditingService(service.id);
    setFormData({
      title: service.title,
      icon: service.icon,
      description: service.description,
      features: [...service.features]
    });
    setIsCreating(false);
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingService(null);
    setFormData({
      title: '',
      icon: 'Briefcase',
      description: '',
      features: ['', '', '', '']
    });
  };

  const handleCancel = () => {
    setEditingService(null);
    setIsCreating(false);
    setFormData({ title: '', icon: 'Briefcase', description: '', features: ['', '', '', ''] });
  };

  const handleSave = async () => {
    try {
      // Filter out empty features
      const cleanedFeatures = formData.features.filter(f => f.trim() !== '');
      
      if (!formData.title || !formData.description || cleanedFeatures.length === 0) {
        toast.error('Please fill all required fields');
        return;
      }

      const serviceData = {
        ...formData,
        features: cleanedFeatures
      };

      if (isCreating) {
        await adminApi.createService(serviceData);
        toast.success('Service created successfully');
      } else {
        await adminApi.updateService(editingService, serviceData);
        toast.success('Service updated successfully');
      }

      loadServices();
      handleCancel();
    } catch (error) {
      toast.error('Failed to save service');
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;

    try {
      await adminApi.deleteService(serviceId);
      toast.success('Service deleted successfully');
      loadServices();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  const addFeature = () => {
    setFormData({ ...formData, features: [...formData.features, ''] });
  };

  if (loading) {
    return (
      <AdminDashboard>
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-500">Loading services...</p>
        </div>
      </AdminDashboard>
    );
  }

  return (
    <AdminDashboard>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Services Management</h1>
            <p className="text-slate-600 mt-1">Manage your service offerings</p>
          </div>
          <Button
            onClick={handleCreate}
            className="bg-gradient-to-r from-[#1E4E9E] to-[#00B050] hover:from-[#00B050] hover:to-[#1E4E9E] text-white"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Service
          </Button>
        </div>

        {/* Create/Edit Form */}
        {(isCreating || editingService) && (
          <Card className="border-2 border-[#1E4E9E]">
            <CardHeader>
              <CardTitle>{isCreating ? 'Create New Service' : 'Edit Service'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Service title"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Icon Name</label>
                <Input
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="Lucide icon name (e.g., Briefcase, Monitor, Zap)"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Description *</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Service description"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Features *</label>
                {formData.features.map((feature, index) => (
                  <Input
                    key={index}
                    value={feature}
                    onChange={(e) => handleFeatureChange(index, e.target.value)}
                    placeholder={`Feature ${index + 1}`}
                    className="mb-2"
                  />
                ))}
                <Button onClick={addFeature} variant="outline" size="sm" className="mt-2">
                  <Plus className="w-4 h-4 mr-1" /> Add Feature
                </Button>
              </div>

              <div className="flex space-x-3">
                <Button onClick={handleSave} className="bg-[#00B050] hover:bg-[#00B050]/90">
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
                <Button onClick={handleCancel} variant="outline">
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Services List */}
        <div className="grid md:grid-cols-2 gap-6">
          {services.map((service) => (
            <Card key={service.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <p className="text-sm text-slate-500 mt-1">Icon: {service.icon}</p>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(service)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(service.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-4">{service.description}</p>
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-slate-700">Features:</p>
                  <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
                    {service.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </AdminDashboard>
  );
};